import * as z from 'zod'

import { ZodStorage } from '@gto/helpers/storage'
import { gtoWizardAppDevUrl, gtoWizardAppUrl } from '@gto/urls'

import { getRuntimeConfig, isProdEnv } from '@/config/runtime-env'

// localStorage (not sessionStorage) so the record survives the originating tab
// being discarded during the authorize round-trip (mobile Safari eviction,
// callback in a new tab). Kept single-use + TTL-bounded, so not a security regression.
const OAUTH_LS_KEY = 'affiliates_oauth_pkce'
const OAUTH_PKCE_MAX_AGE_MS = 10 * 60 * 1000

// The per-code idempotency marker stays in sessionStorage: per-tab is correct
// for the StrictMode/double-exchange guard — it must NOT be shared across tabs.
const SESSION_KEY_PROCESSED_CODE_PREFIX = 'affiliates_oauth_processed_'
const OAUTH_REDIRECT_PATH = '/login/oauth'
const OAUTH_SCOPE = 'read affiliates:write'

// Pre-migration builds wrote PKCE state to these sessionStorage keys. A user
// mid-login when this ships started on the old code; clear them so the orphaned
// records don't linger after the localStorage migration.
const LEGACY_SESSION_KEYS = ['affiliates_oauth_state', 'affiliates_oauth_code_verifier'] as const

const oauthPkceRecordSchema = z.object({
  state: z.string().min(1),
  codeVerifier: z.string().min(1),
  // .int().nonnegative() rejects a corrupted timestamp before the TTL check trusts it.
  createdAt: z.number().int().nonnegative(),
})

// affiliates-web has no central typed-storage registry (unlike gto-wizard-web's
// storage.ts), so this is the app-local ZodStorage instance for the one PKCE
// key — every read/write of OAUTH_LS_KEY goes through it and is Zod-validated
// at the storage boundary.
//
// The backing storage is resolved on each call (not captured at construction),
// matching the prior `localStorage.getItem(...)` reads which always hit the
// live global. clearOAuthStorage also touches the live `sessionStorage` global
// directly, so binding here to a snapshot would let the two stores diverge.
const liveLocalStorage: Storage = {
  getItem: (key) => localStorage.getItem(key),
  setItem: (key, value) => {
    localStorage.setItem(key, value)
  },
  removeItem: (key) => {
    localStorage.removeItem(key)
  },
  clear: () => {
    localStorage.clear()
  },
  key: (index) => localStorage.key(index),
  get length() {
    return localStorage.length
  },
}
const oauthStorageSchema = { [OAUTH_LS_KEY]: oauthPkceRecordSchema }
const oauthStorage = new ZodStorage(oauthStorageSchema, liveLocalStorage)

// 'expired' distinguishes a benign timeout from 'missing' so the UI shows the
// accurate message for each; 'valid' carries both fields (state without verifier
// is unrepresentable).
export type StoredOAuthState =
  | { status: 'valid'; state: string; codeVerifier: string }
  | { status: 'expired' }
  | { status: 'missing' }

/**
 * Reads the stored PKCE record exactly once, validates it, and clears it on any
 * non-valid outcome. A single read closes the TOCTOU window a two-getter design
 * left open (the record could be cleared between a state read and a verifier read).
 */
export function readStoredOAuthState(): StoredOAuthState {
  // One read through the typed-storage boundary: absent, corrupt-JSON, and
  // schema-violating records all surface as `success: false` and map to
  // 'missing' (clearing storage), matching the prior hand-rolled behavior.
  const result = oauthStorage.getItem(OAUTH_LS_KEY)
  if (!result.success) {
    clearOAuthStorage()

    return { status: 'missing' }
  }

  // A createdAt in the future (clock skew or a tampered record) makes the age
  // negative, so it would never exceed the TTL and read back as perpetually valid.
  // Treat it as expired on the same path as a genuine timeout.
  const age = Date.now() - result.data.createdAt
  if (age < 0 || age > OAUTH_PKCE_MAX_AGE_MS) {
    clearOAuthStorage()

    return { status: 'expired' }
  }

  return {
    status: 'valid',
    state: result.data.state,
    codeVerifier: result.data.codeVerifier,
  }
}

/**
 * Returns the per-environment OAuth client_id derived from `classifyEnv()` in
 * runtime-env.ts. Throws if the resolved id is empty — surfaces misconfiguration
 * loudly on the first login attempt rather than shipping a silently-broken build
 * that fails with an opaque `invalid_client` error from the authorization server.
 */
export function getOAuthClientId(): string {
  const clientId = getRuntimeConfig().oauthClientId

  if (clientId.length === 0) {
    throw new Error(
      'OAuth client_id is empty for this environment. Populate PROD_OAUTH_CLIENT_ID / DEV_OAUTH_CLIENT_ID in apps/affiliates-web/src/config/runtime-env.ts with the values provisioned on the `oauth.Application` table in Django admin.',
    )
  }

  return clientId
}

function generateRandomBase64Url(byteLength: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength))

  // base64url encoding: standard base64 with +→- /→_ and no padding
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

export async function computeCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const hash = await crypto.subtle.digest('SHA-256', data)

  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

/**
 * Builds the full OAuth 2.0 authorization URL for the affiliates portal.
 * Generates a fresh PKCE code_verifier + code_challenge (S256) and a random
 * state, persists both in localStorage for the callback to verify, and
 * returns the URL to redirect the user to on app.gtowizard.com.
 */
export async function buildOAuthRedirectUrl(): Promise<string> {
  const state = generateRandomBase64Url(32)
  const codeVerifier = generateRandomBase64Url(64)
  const codeChallenge = await computeCodeChallenge(codeVerifier)

  oauthStorage.setItem(OAUTH_LS_KEY, { state, codeVerifier, createdAt: Date.now() })

  const origin = isProdEnv() ? gtoWizardAppUrl : gtoWizardAppDevUrl
  const url = new URL(`${origin}login`)

  const redirectUri = getOAuthRedirectUri()

  url.searchParams.set('client_id', getOAuthClientId())
  url.searchParams.set('redirect_uri', redirectUri)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('scope', OAUTH_SCOPE)
  url.searchParams.set('code_challenge', codeChallenge)
  url.searchParams.set('code_challenge_method', 'S256')
  url.searchParams.set('state', state)

  return url.toString()
}

export function getOAuthRedirectUri(): string {
  return window.location.origin + OAUTH_REDIRECT_PATH
}

/**
 * Returns the sessionStorage key used to mark a given authorization code as
 * in-flight. Defends against double-exchange when the loader is re-rendered
 * (React StrictMode, fast back-nav) before the token exchange POST resolves
 * and clearOAuthStorage runs. After clearOAuthStorage clears the verifier,
 * any subsequent re-render fails the verifier check first — the marker is
 * the only guard during the brief window where the verifier still exists
 * and a second POST could be issued for the same code.
 */
export function getProcessedCodeKey(code: string): string {
  return `${SESSION_KEY_PROCESSED_CODE_PREFIX}${code}`
}

/**
 * Clears all OAuth state after the flow completes: the localStorage PKCE record,
 * every per-code idempotency marker, and the legacy pre-migration sessionStorage
 * keys, so no store accumulates stale records across repeated sign-in attempts.
 */
export function clearOAuthStorage(): void {
  oauthStorage.removeItem(OAUTH_LS_KEY)

  for (const key of LEGACY_SESSION_KEYS) {
    sessionStorage.removeItem(key)
  }

  const keysToRemove: string[] = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (key !== null && key.startsWith(SESSION_KEY_PROCESSED_CODE_PREFIX)) {
      keysToRemove.push(key)
    }
  }
  for (const key of keysToRemove) {
    sessionStorage.removeItem(key)
  }
}

/**
 * Length-independent string equality — defence-in-depth against timing side
 * channels on OAuth `state`. An attacker with a precise clock could otherwise
 * probe the first mismatching byte of the stored `state` via `!==`.
 */
export function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) {
    return false
  }
  let diff = 0
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i)
  }

  return diff === 0
}
